import axios from '../../../api/axios';

/**
 * Pre-Moveout Inspection API (staff side).
 * Server contract: /v2/pre-moveout-inspections — every response is { success, message?, data, meta? }.
 */
export const BASE = '/v2/pre-moveout-inspections';

export const ENDPOINTS = {
  LIST: BASE,
  CREATE: BASE,
  UNITS: `${BASE}/units`,
  VENDOR_SEARCH: `${BASE}/vendors/search`,
  ME: `${BASE}/me`,
  SIGNED_URLS: `${BASE}/signed-urls`,
  BY_ID: `${BASE}/:id`,
  HISTORY: `${BASE}/:id/history`,
  SUBMIT: `${BASE}/:id/submit`,
  UPLOAD_REQUEST: `${BASE}/:id/upload-request`,
  CONFIRM_UPLOAD: `${BASE}/:id/confirm-upload`,
  ATTACHMENTS: `${BASE}/:id/attachments`,
  CANCEL: `${BASE}/:id/cancel`,
  REOPEN: `${BASE}/:id/reopen`,
  COMPLETE: `${BASE}/:id/complete`,
  ASSIGNMENTS: `${BASE}/:id/assignments`,
  ASSIGNMENT_RESEND: `${BASE}/:id/assignments/:aid/resend`,
  ASSIGNMENT_CANCEL: `${BASE}/:id/assignments/:aid/cancel`,
  ASSIGNMENT_APPROVE: `${BASE}/:id/assignments/:aid/approve`,
  ASSIGNMENT_DECLINE: `${BASE}/:id/assignments/:aid/decline`,
};

const withId = (tpl, id, aid) => tpl.replace(':id', encodeURIComponent(id)).replace(':aid', encodeURIComponent(aid || ''));

/**
 * Central error handler. Throws an Error whose message is the server message when
 * available. When the server returned `problems: [string]` (submit validation)
 * they are attached as `err.problems`; the HTTP status is attached as `err.status`.
 */
export const handleApiError = (error, fallback) => {
  const data = error && error.response ? error.response.data : null;
  const message =
    (data && data.message) || (error && error.message) || fallback || 'An unexpected error occurred';
  const err = new Error(message);
  if (data && Array.isArray(data.problems) && data.problems.length) err.problems = data.problems;
  if (error && error.response && error.response.status) err.status = error.response.status;
  err.cause = error;
  console.error(fallback || message, error);
  throw err;
};

/* --------------------------- Reads ---------------------------------- */

/** GET / → { data: [doc], meta: { categories, ratings } } */
export const listPreMoveoutInspections = async (params = {}) => {
  try {
    const clean = {};
    Object.keys(params).forEach((k) => {
      if (params[k] !== undefined && params[k] !== null && params[k] !== '') clean[k] = params[k];
    });
    const res = await axios.get(ENDPOINTS.LIST, { params: clean });
    return { data: res.data?.data || [], meta: res.data?.meta || {} };
  } catch (error) {
    return handleApiError(error, 'Failed to load pre-moveout inspections');
  }
};

/** GET /units?propertyId=&buildingName= → [{ unitNo, tenant }] */
export const getPreMoveoutUnits = async (propertyId, buildingName) => {
  try {
    const res = await axios.get(ENDPOINTS.UNITS, { params: { propertyId, buildingName } });
    return res.data?.data || [];
  } catch (error) {
    return handleApiError(error, 'Failed to load units');
  }
};

/** GET /vendors/search?q=&kind= → [{ name, email, source, vendor_id?, category? }] */
export const searchPreMoveoutVendors = async (q, kind) => {
  try {
    const res = await axios.get(ENDPOINTS.VENDOR_SEARCH, { params: { q: q || '', kind: kind || '' } });
    return res.data?.data || [];
  } catch (error) {
    return handleApiError(error, kind === 'internal' ? 'Failed to search technical staff' : 'Failed to search vendors');
  }
};

/** GET /me → { isStaff, isVendor, canApprove } */
export const getPreMoveoutMe = async () => {
  try {
    const res = await axios.get(ENDPOINTS.ME);
    return res.data?.data || {};
  } catch (error) {
    return handleApiError(error, 'Failed to load permissions');
  }
};

/** GET /:id → { data: doc, meta: { canApprove, categories, ratings } } */
export const getPreMoveoutInspection = async (id) => {
  try {
    const res = await axios.get(withId(ENDPOINTS.BY_ID, id));
    return { data: res.data?.data || null, meta: res.data?.meta || {} };
  } catch (error) {
    return handleApiError(error, 'Failed to load the inspection');
  }
};

/** GET /:id/history → [entries newest first] */
export const getPreMoveoutHistory = async (id) => {
  try {
    const res = await axios.get(withId(ENDPOINTS.HISTORY, id));
    return res.data?.data || [];
  } catch (error) {
    return handleApiError(error, 'Failed to load history');
  }
};

/** POST /signed-urls { keys } → { [key]: url|null } */
export const getPreMoveoutSignedUrls = async (keys) => {
  if (!Array.isArray(keys) || !keys.length) return {};
  try {
    const res = await axios.post(ENDPOINTS.SIGNED_URLS, { keys });
    return res.data?.data || {};
  } catch (error) {
    return handleApiError(error, 'Failed to load media previews');
  }
};

/* --------------------------- Writes --------------------------------- */

/** POST / → doc (201). Pass submit:true in payload to validate + submit in one call. */
export const createPreMoveoutInspection = async (payload) => {
  try {
    const res = await axios.post(ENDPOINTS.CREATE, payload, { headers: { 'Content-Type': 'application/json' } });
    return res.data?.data;
  } catch (error) {
    return handleApiError(error, 'Failed to create the inspection');
  }
};

/** PATCH /:id → doc. Include each existing repair's _id so its attachments are kept. */
export const updatePreMoveoutInspection = async (id, payload) => {
  try {
    const res = await axios.patch(withId(ENDPOINTS.BY_ID, id), payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data?.data;
  } catch (error) {
    return handleApiError(error, 'Failed to save the inspection');
  }
};

/** POST /:id/submit → doc (400 with err.problems when evidence is missing) */
export const submitPreMoveoutInspection = async (id) => {
  try {
    const res = await axios.post(withId(ENDPOINTS.SUBMIT, id), {});
    return res.data?.data;
  } catch (error) {
    return handleApiError(error, 'The inspection could not be submitted');
  }
};

/** DELETE /:id (drafts only) */
export const deletePreMoveoutInspection = async (id) => {
  try {
    const res = await axios.delete(withId(ENDPOINTS.BY_ID, id));
    return res.data;
  } catch (error) {
    return handleApiError(error, 'Failed to delete the inspection');
  }
};

/** POST /:id/cancel { reason } → doc */
export const cancelPreMoveoutInspection = async (id, reason) => {
  try {
    const res = await axios.post(withId(ENDPOINTS.CANCEL, id), { reason });
    return res.data?.data;
  } catch (error) {
    return handleApiError(error, 'Failed to cancel the inspection');
  }
};

/** POST /:id/reopen → doc */
export const reopenPreMoveoutInspection = async (id) => {
  try {
    const res = await axios.post(withId(ENDPOINTS.REOPEN, id), {});
    return res.data?.data;
  } catch (error) {
    return handleApiError(error, 'Failed to reopen the inspection');
  }
};

/** POST /:id/complete { note? } → doc */
export const completePreMoveoutInspection = async (id, note) => {
  try {
    const res = await axios.post(withId(ENDPOINTS.COMPLETE, id), note ? { note } : {});
    return res.data?.data;
  } catch (error) {
    return handleApiError(error, 'Failed to mark the inspection completed');
  }
};

/* --------------------------- Media ---------------------------------- */

/** POST /:id/upload-request { target, repairId?, fileName, contentType } → { presignedUrl, key } */
export const requestPreMoveoutUploadUrl = async (id, { target, repairId, fileName, contentType }) => {
  try {
    const body = { target, fileName, contentType };
    if (target === 'repair') body.repairId = repairId;
    const res = await axios.post(withId(ENDPOINTS.UPLOAD_REQUEST, id), body);
    return res.data?.data;
  } catch (error) {
    return handleApiError(error, 'Failed to prepare the file upload');
  }
};

/** POST /:id/confirm-upload { target, repairId?, key } → doc */
export const confirmPreMoveoutUpload = async (id, { target, repairId, key }) => {
  try {
    const body = { target, key };
    if (target === 'repair') body.repairId = repairId;
    const res = await axios.post(withId(ENDPOINTS.CONFIRM_UPLOAD, id), body);
    return res.data?.data;
  } catch (error) {
    return handleApiError(error, 'Failed to confirm the file upload');
  }
};

/** DELETE /:id/attachments { target, repairId?, key } → doc */
export const deletePreMoveoutAttachment = async (id, { target, repairId, key }) => {
  try {
    const body = { target, key };
    if (target === 'repair') body.repairId = repairId;
    const res = await axios.delete(withId(ENDPOINTS.ATTACHMENTS, id), { data: body });
    return res.data?.data;
  } catch (error) {
    return handleApiError(error, 'Failed to remove the file');
  }
};

/**
 * Convenience: upload-request → PUT to S3 → confirm-upload. Resolves with the
 * updated doc returned by confirm-upload. `file` is a browser File/Blob.
 */
export const uploadPreMoveoutFile = async (id, { target, repairId, file }) => {
  const contentType = file.type || 'application/octet-stream';
  const { presignedUrl, key } = await requestPreMoveoutUploadUrl(id, {
    target,
    repairId,
    fileName: file.name,
    contentType,
  });
  const putRes = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  if (!putRes.ok) {
    const err = new Error(`Upload of ${file.name} failed (${putRes.status})`);
    err.status = putRes.status;
    throw err;
  }
  return confirmPreMoveoutUpload(id, { target, repairId, key });
};

/* --------------------------- Assignments ---------------------------- */

/** POST /:id/assignments { kind, vendor_email, vendor_name, vendor_id?, scope, estimate_price? } → doc */
export const createPreMoveoutAssignment = async (id, payload) => {
  try {
    const res = await axios.post(withId(ENDPOINTS.ASSIGNMENTS, id), payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return { ...res.data?.data, deliveryMessage: res.data?.message };
  } catch (error) {
    return handleApiError(error, 'Failed to assign the vendor');
  }
};

/** POST /:id/assignments/:aid/resend → doc */
export const resendPreMoveoutAssignment = async (id, aid) => {
  try {
    const res = await axios.post(withId(ENDPOINTS.ASSIGNMENT_RESEND, id, aid), {});
    return res.data?.data;
  } catch (error) {
    return handleApiError(error, 'Failed to resend the quote request');
  }
};

/** POST /:id/assignments/:aid/cancel { reason } → doc */
export const cancelPreMoveoutAssignment = async (id, aid, reason) => {
  try {
    const res = await axios.post(withId(ENDPOINTS.ASSIGNMENT_CANCEL, id, aid), { reason });
    return res.data?.data;
  } catch (error) {
    return handleApiError(error, 'Failed to cancel the assignment');
  }
};

/** POST /:id/assignments/:aid/approve { amount?, note? } → doc (403 unless canApprove) */
export const approvePreMoveoutAssignment = async (id, aid, { amount, note } = {}) => {
  try {
    const body = {};
    if (amount !== undefined && amount !== null && amount !== '') body.amount = Number(amount);
    if (note) body.note = note;
    const res = await axios.post(withId(ENDPOINTS.ASSIGNMENT_APPROVE, id, aid), body);
    return res.data?.data;
  } catch (error) {
    return handleApiError(error, 'Failed to approve the quote');
  }
};

/** POST /:id/assignments/:aid/decline { reason } → doc (403 unless canApprove) */
export const declinePreMoveoutAssignment = async (id, aid, reason) => {
  try {
    const res = await axios.post(withId(ENDPOINTS.ASSIGNMENT_DECLINE, id, aid), { reason });
    return res.data?.data;
  } catch (error) {
    return handleApiError(error, 'Failed to decline the quote');
  }
};

export const getTenantChecklist = async (params) => {
  try { return (await axios.get(`${BASE}/tenant-checklist`, { params })).data.data; }
  catch (error) { return handleApiError(error, 'Could not load tenants'); }
};
export const sendTenantChecklist = async (payload) => {
  try { return (await axios.post(`${BASE}/tenant-checklist`, payload)).data.data; }
  catch (error) { return handleApiError(error, 'Could not send checklists'); }
};

export const updatePreMoveoutAssignment = async (id, aid, payload) => {
  try { const res = await axios.patch(`${BASE}/${encodeURIComponent(id)}/assignments/${encodeURIComponent(aid)}`, payload); return { ...res.data.data, deliveryMessage: res.data.message }; }
  catch (error) { return handleApiError(error, 'Could not update the request'); }
};


export const getPremoveoutNotices = async (params) => {
  try { const response = await axios.get(`${BASE}/notices`, { params }); return response.data.data; }
  catch (error) { return handleApiError(error, 'Failed to load premoveout notices'); }
};
export const sendPremoveoutNotice = async (payload) => {
  try { const response = await axios.post(`${BASE}/notices`, payload, { timeout: 180000 }); return response.data.data; }
  catch (error) { return handleApiError(error, 'Failed to send premoveout notice'); }
};
